import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, History, Play } from 'lucide-react';

import {
  listSyncLog,
  runShipedgeReportsSync,
  type ShipedgeReportSyncLogRow,
  type ShipedgeSyncStatus,
} from '@/api/shipedgeReports.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ShipedgeAccessDenied, ShipedgePageHeader } from './_chrome';
import { useShipedgeAdminGate } from './_gate';

/**
 * Sync Log page. Phase 15 Day 7.
 *
 * Recent sync history (most recent 25 by default) + a "Run sync now"
 * button that opens a date-range modal. POST /sync goes through the
 * `runNightlySync` orchestrator with `triggeredBy: 'admin_manual'`.
 */

export function SyncLogPage() {
  const { canAccess } = useShipedgeAdminGate();
  const [runOpen, setRunOpen] = useState(false);

  const query = useQuery({
    queryKey: ['shipedge-sync-log'],
    queryFn: () => listSyncLog(25),
    enabled: canAccess,
    refetchInterval: 30_000, // soft refresh — catches the cron tick
  });

  if (!canAccess) return <ShipedgeAccessDenied />;

  const rows = query.data ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <ShipedgePageHeader
        title="Sync Log"
        description="Recent ShipEdge report pulls. Cron runs nightly at 02:00 ET. Use Run sync now to backfill a window or recover from a failed night."
        actions={
          <Button
            onClick={() => {
              setRunOpen(true);
            }}
          >
            <Play className="size-4" />
            Run sync now
          </Button>
        }
      />

      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : query.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load sync log</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {query.error instanceof Error ? query.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <History className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No syncs recorded yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              The first cron tick lands at 02:00 ET. Or click Run sync now to kick a backfill.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="border-b border-kw-border bg-kw-bg/40 text-left text-[11px] uppercase tracking-wide text-kw-muted">
                <th className="px-4 py-2">Started</th>
                <th className="px-4 py-2">Endpoint</th>
                <th className="px-4 py-2">Range</th>
                <th className="px-4 py-2">Trigger</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Rows</th>
                <th className="px-4 py-2 text-right">Duration</th>
                <th className="px-4 py-2">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kw-border">
              {rows.map((row) => (
                <SyncLogRow key={row.id} row={row} />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <RunSyncModal
        open={runOpen}
        onClose={() => {
          setRunOpen(false);
        }}
      />
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function SyncLogRow({ row }: { row: ShipedgeReportSyncLogRow }) {
  return (
    <tr className="text-kw-shark hover:bg-kw-bg/30">
      <td className="whitespace-nowrap px-4 py-2 text-kw-muted">{formatDateTime(row.startedAt)}</td>
      <td className="px-4 py-2 font-mono text-[12px]">{row.endpoint}</td>
      <td className="whitespace-nowrap px-4 py-2 text-kw-muted">
        {row.startDate.slice(0, 10)} → {row.endDate.slice(0, 10)}
      </td>
      <td className="px-4 py-2 text-[12px]">{row.triggeredBy}</td>
      <td className="px-4 py-2">
        <StatusBadge status={row.status} />
      </td>
      <td className="px-4 py-2 text-right tabular-nums">{row.rowsIngested.toLocaleString()}</td>
      <td className="px-4 py-2 text-right tabular-nums text-kw-muted">
        {row.durationMs === null ? '—' : `${(row.durationMs / 1000).toFixed(1)}s`}
      </td>
      <td className="max-w-md truncate px-4 py-2 text-kw-danger-text" title={row.error ?? undefined}>
        {row.error ?? ''}
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: ShipedgeSyncStatus }) {
  const styles: Record<ShipedgeSyncStatus, string> = {
    success: 'bg-kw-emerald-light text-kw-tag-text',
    pending: 'bg-kw-bg-blue text-kw-cerulean',
    failed: 'bg-kw-danger-bg text-kw-danger-text',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

function RunSyncModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const yesterday = defaultYesterday();
  const [startDate, setStartDate] = useState(yesterday);
  const [endDate, setEndDate] = useState(yesterday);

  const mutation = useMutation({
    mutationFn: () => runShipedgeReportsSync({ startDate, endDate }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['shipedge-sync-log'] });
      onClose();
      setStartDate(yesterday);
      setEndDate(yesterday);
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!startDate || !endDate) return;
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? () => undefined : onClose}
      title="Run ShipEdge sync now"
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-[13px] text-kw-muted">
          Pulls Adrian&apos;s reports for the chosen window and upserts them into the local cache.
          Adrian&apos;s API caps at 90 days. Single attempt, no retry — matches the nightly cron&apos;s
          behavior.
        </p>
        {mutation.isPending ? (
          <p className="rounded-kw-md border border-kw-warning-text/40 bg-kw-warning-bg p-3 text-[12px] text-kw-warning-text">
            Running… the upstream takes 30 seconds to 2+ minutes per endpoint. Leave this open — it closes
            itself when both pulls finish.
          </p>
        ) : null}
        <Input
          label="Start date"
          name="startDate"
          type="date"
          required
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
          }}
        />
        <Input
          label="End date"
          name="endDate"
          type="date"
          required
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
          }}
        />
        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {mutation.error instanceof Error ? mutation.error.message : 'Sync failed'}
            </p>
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!startDate || !endDate}>
            <Play className="size-4" />
            Run sync
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function defaultYesterday(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${String(yyyy)}-${mm}-${dd}`;
}
